import { InstitutionView } from "@bitwarden/web-vault/app/models/view/institution/institution.view";
import { InstitutionAccountView } from "@bitwarden/web-vault/app/models/view/institution/institution-account.view";
import { DefaultInstitutionStoreModel } from "@bitwarden/web-vault/app/models/store/institution.store.model";
import { getLocationCurrencyFromCountryCode } from "@bitwarden/web-vault/app/models/types/location-currency.types";
import {
  applyOtherSwiftDefaults,
  getDefaultAccount,
  isOtherInstitutionId,
} from "./default-institution-account";

export const PLUGIN_INSTITUTION_ID_PREFIX = "plugin-institution-id-";

/** Not country-scoped (unlike createOtherInstitution) — resolvePluginInstitution (used by
 * plugin-config.component.ts) looks this up by plugin id alone. */
export const getPluginInstitutionId = (pluginId: string): string =>
  `${PLUGIN_INSTITUTION_ID_PREFIX}${pluginId}`;

/** Plugin institutions (e.g. Opal) stay in the catalog so lookups by id keep working
 * (scenario-data-aggregrator, best-scenario-stepper), but their accounts are only created through
 * the plugin flow. Name-based lookups for manual accounts/institutions must skip them, or a user's
 * own manual "Opal" resolves to (or is blocked by) the plugin's. */
export const isPluginInstitution = (institution: { id: string }): boolean =>
  institution.id.startsWith(PLUGIN_INSTITUTION_ID_PREFIX);

/**
 * Synthetic institution for a plugin-created account, named after the plugin itself (e.g. "Opal")
 * instead of the generic "Other" fallback every plugin used before it opts in via
 * `PluginMetadata.institutionName` (plugin-metadata.ts) — same not-fetched-from-the-server,
 * appended-to-the-catalog pattern as createOtherInstitution/getGlossVaultInstitution, so account
 * rows can resolve its name by id like any real institution (external.institutions.store.collection.ts).
 */
export const getPluginInstitution = (
  pluginId: string,
  institutionName: string,
  countryCode: string,
): InstitutionView => {
  const institution = new InstitutionView({
    ...DefaultInstitutionStoreModel,
    id: getPluginInstitutionId(pluginId),
  });
  institution.name = institutionName;
  // Same placeholder swift as "Other"; only the id, name and product differ.
  applyOtherSwiftDefaults(institution, countryCode);
  // Product name equals the institution name so account.table.ts's accountProductName (which
  // prepends the institution name unless the product name already starts with it) renders as
  // just "Opal", not "Opal Other"/duplicated.
  const account = getDefaultAccount(
    institution.id,
    getLocationCurrencyFromCountryCode(countryCode),
  );
  account.name = institutionName;
  institution.availableAccounts[0] = account;
  return institution;
};

/**
 * plugin-config.component.ts's institution lookup: try this plugin's own institution (personal,
 * then catalog) before ever falling back to the generic "Other" one — checking personal-generic
 * before catalog-specific would mean any user who already has an "Other" institution saved (true
 * for almost everyone, since manual accounts and any prior plugin/gift-card use already save one)
 * never picks up the plugin-specific institution at all. Only once both plugin-specific lookups
 * miss do we fall back to generic "Other" — e.g. if the institutions catalog hasn't finished its
 * (network-dependent) fetch yet on a fresh app load, the plugin-specific one won't be in it, but
 * "Other" reliably already is (any prior use of any plugin saves it into the user's personal
 * institutions, which loads from the local vault, not the network). Falling back beats hard-failing
 * the whole flow over a cosmetic name.
 */
export async function resolvePluginInstitution(
  pluginId: string,
  institutionName: string | undefined,
  personalInstitutions: InstitutionView[],
  catalogInstitutions: InstitutionView[],
  savePersonalInstitution: (institution: InstitutionView) => Promise<unknown>,
): Promise<InstitutionView> {
  const matchesPlugin = (inst: { id: string }) =>
    !!institutionName && inst.id === getPluginInstitutionId(pluginId);
  const matchesGeneric = (inst: { id: string }) => isOtherInstitutionId(inst.id);

  for (const matches of [matchesPlugin, matchesGeneric]) {
    const personal = personalInstitutions.find(matches);
    if (personal) {
      return personal;
    }
    const catalog = catalogInstitutions.find(matches);
    if (catalog) {
      await savePersonalInstitution(catalog);
      return catalog;
    }
  }

  // Institutions catalog hasn't loaded at all yet — vanishingly rare, but saving `undefined`
  // would otherwise crash with an opaque error.
  throw new Error("Could not resolve an institution to link this plugin account to.");
}

/** Every institution resolvePluginInstitution can return (Other/Gloss Vault/per-plugin) has
 * exactly one synthetic product, so the first entry is always the right one. */
export function firstAvailableProduct(
  institution: { availableAccounts?: InstitutionAccountView[] } | null | undefined,
): InstitutionAccountView | undefined {
  return institution?.availableAccounts?.[0];
}
